/* ================================================================
   EffectifsClassesPage.tsx — Effectifs des classes
   V7 — Parité avec StudentsPage : même endpoint /students paginé,
   mêmes filtres (active_only) → effectifs identiques partout.
   ================================================================ */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  BarChart3, Printer, FileSpreadsheet, Download,
  Loader2, Settings, Calendar, CheckSquare, Square, Users,
  Building2, Save, ChevronDown, ChevronUp
} from "lucide-react";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== HELPERS ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === "object") {
    const keys = ["data", "items", "results", "students", "eleves", "rows"];
    for (const k of keys) {
      if (Array.isArray(res[k])) return res[k];
      if (res[k] && typeof res[k] === "object" && !Array.isArray(res[k])) {
        for (const k2 of keys) {
          if (Array.isArray(res[k][k2])) return res[k][k2];
        }
      }
    }
  }
  return [];
};

const AUTH_STORAGE_KEY = "auth-storage";
const SELECTED_YEAR_ID_KEY = "lybibal-selected-year-id";
const SELECTED_YEAR_LABEL_KEY = "lybibal-selected-year-label";
const SELECTED_YEAR_EVENT = "lybibal-year-change";

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const state = parsed?.state || parsed;
      if (state?.user?.school_id) return Number(state.user.school_id);
    }
  } catch (e) { console.warn("Failed to get school_id:", e); }
  return null;
};

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yearId) return Number(yearId);
  } catch (e) { console.warn("Failed to get year_id:", e); }
  return null;
};

function getValue(obj: any, ...keys: string[]): any {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    for (const subKey of Object.keys(obj)) {
      const sub = obj[subKey];
      if (sub && typeof sub === "object" && !Array.isArray(sub)) {
        if (sub[key] !== undefined && sub[key] !== null) return sub[key];
      }
    }
  }
  return undefined;
}

function getStr(obj: any, ...keys: string[]): string {
  const v = getValue(obj, ...keys);
  return v !== undefined && v !== null ? String(v) : "";
}

function extractBool(obj: any, ...keys: string[]): boolean {
  if (!obj || typeof obj !== "object") return false;
  const truthy = [true, "true", "True", "TRUE", 1, "1", "yes", "Yes", "YES", "oui", "Oui", "OUI"];
  const falsy = [false, "false", "False", "FALSE", 0, "0", "no", "No", "NO", "non", "Non", "NON"];
  for (const key of keys) {
    const v = obj[key];
    if (truthy.includes(v)) return true;
    if (falsy.includes(v)) return false;
    for (const subKey of Object.keys(obj)) {
      const sub = obj[subKey];
      if (sub && typeof sub === "object" && !Array.isArray(sub)) {
        const sv = sub[key];
        if (truthy.includes(sv)) return true;
        if (falsy.includes(sv)) return false;
      }
    }
  }
  return false;
}

/** Cherche is_repeating dans l'objet et ses sous-objets courants (student, enrollments, etc.) */
function findRepeating(obj: any): boolean {
  if (!obj || typeof obj !== "object") return false;
  const candidates = [
    obj,
    obj.student,
    obj.enrollments?.[0],
    obj.enrollment,
    obj.student_enrollments?.[0],
    obj.school_enrollments?.[0],
  ];
  for (const cand of candidates) {
    if (cand && typeof cand === "object") {
      const v = cand.is_repeating;
      if (v === true || v === "true" || v === 1 || v === "1" || v === "yes" || v === "oui") return true;
      if (v === false || v === "false" || v === 0 || v === "0" || v === "no" || v === "non") return false;
    }
  }
  return false;
}

function calculateAge(birthDate: string): number {
  if (!birthDate) return 0;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age > 0 ? age : 0;
}

function downloadCSV(filename: string, rows: string[][], headerRows?: string[][]) {
  const allRows = headerRows ? [...headerRows, ...rows] : rows;
  const csv = allRows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function downloadExcelData(filename: string, data: any[]) {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const rows = [headers, ...data.map(row => headers.map(h => row[h] ?? ""))];
  downloadCSV(filename, rows);
}

function headerEnDelegation(fr: string): string {
  const map: Record<string, string> = {
    "DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES": "DIVISIONAL DELEGATION FOR SECONDARY EDUCATION",
  };
  return map[fr] || fr;
}


// ==================== TYPES ====================
interface SchoolHeader {
  republic_fr: string;
  republic_en: string;
  motto_fr: string;
  motto_en: string;
  region: string;
  department: string;
  delegation: string;
  sub_division: string;
  school_name: string;
  school_name_en: string;
  address: string;
  po_box: string;
  logo_url: string;
}

interface Student {
  id: number;
  class_id?: number;
  class_name?: string;
  first_name: string;
  last_name: string;
  gender: string;
  is_repeating?: boolean;
  is_new?: boolean;
  status?: string;   // V3 : statut (démissionnaires exclus des effectifs)
}

interface ClassStats {
  class_id: number;
  class_name: string;
  level_name: string;
  total: number;
  male: number;
  female: number;
  repeating_yes: number;
  repeating_no: number;
  new_student: number;
  old_student: number;
}

/** Cherche is_new dans l'objet et ses sous-objets */
function findIsNew(obj: any): boolean {
  if (!obj || typeof obj !== "object") return false;
  const candidates = [obj, obj.student, obj.enrollments?.[0], obj.enrollment, obj.student_enrollments?.[0]];
  for (const cand of candidates) {
    if (cand && typeof cand === "object") {
      const v = cand.is_new ?? cand.nouveau ?? cand.new_student;
      if (v === true || v === "true" || v === 1 || v === "1" || v === "yes" || v === "oui") return true;
    }
  }
  return false;
}

/** V7 — la classe affichée = classe d'inscription réelle renvoyée par l'API. */
function normalizeStudent(s: any, classId?: number, className?: string): Student {
  const stu = s.student || s;
  const resolvedClassId = classId ?? (() => {
    const v = getValue(s, "class_id", "student_class_id", "classe_id", "classId");
    if (v !== undefined && v !== null) return Number(v);
    const clsObj = s.class || s.classe || stu.class;
    if (clsObj && typeof clsObj === "object" && clsObj.id !== undefined && clsObj.id !== null) return Number(clsObj.id);
    const enr = s.enrollment || s.enrollments?.[0] || stu.enrollment;
    if (enr && typeof enr === "object" && enr.class_id !== undefined && enr.class_id !== null) return Number(enr.class_id);
    return undefined;
  })();
  const clsObj = s.class || s.classe || stu.class;
  const nameFromStr = getStr(s, "class_name", "classe_name", "className");
  const nameFromObj = clsObj && typeof clsObj === "object" ? getStr(clsObj, "name", "label") : "";
  return {
    id: stu.id ?? s.id ?? 0,
    class_id: resolvedClassId,
    class_name: className ?? (nameFromStr || nameFromObj || s.class_name || stu.class_name || undefined),
    first_name: stu.first_name || stu.prenom || s.first_name || s.prenom || "",
    last_name: stu.last_name || stu.nom || s.last_name || s.nom || "",
    gender: stu.gender || stu.sex || stu.sexe || s.gender || s.sex || s.sexe || "",
    is_repeating: findRepeating(s),
    is_new: findIsNew(s),
    status: getStr(stu, "status") || getStr(s, "status"),
  };
}

export default function EffectifsClassesPage() {
  const { lang } = useLang();

  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null);

  const t = {
    fr: {
      title: "Effectifs des classes",
      subtitle: "Situation des effectifs par classe — Année académique",
      listTitle: "EFFECTIFS DES CLASSES",
      configTitle: "Configuration",
      section: "Section", cycle: "Cycle", level: "Niveau",
      allSections: "Toutes les sections", allCycles: "Tous les cycles",
      allLevels: "Tous les niveaux",
      academicYear: "Année académique",
      exportPDF: "PDF", exportExcel: "Excel", exportExcelData: "Excel Data",
      noData: "Aucune donnée", loading: "Chargement...",
      noYearSelected: "Aucune année scolaire sélectionnée",
      yearRequired: "Veuillez sélectionner une année scolaire dans le header",
      classCol: "Classe", total: "Total", levelTotal: "Niveau", grandTotal: "Secondaire",
      sex: "SEXE", male: "Masculin", female: "Feminin",
      repeating: "REDOUBLANT", repYes: "Oui", repNo: "Non",
      situation: "SITUATION", newStudent: "Nouveau", oldStudent: "Ancien",
      selectClasses: "Sélectionner les classes",
      selectAll: "Tout sélectionner", deselectAll: "Tout désélectionner",
      selectedCount: "classe(s) sélectionnée(s)",
      status: "Statut", enrolled: "Inscrits", globalTotal: "Effectif Global",
      headerTitle: "En-tête de l'établissement",
      headerDesc: "Personnalisez l'en-tête qui apparaît sur chaque document",
      schoolName: "Nom de l'établissement",
      schoolNameEn: "Nom (EN)",
      region: "Région",
      department: "Département",
      delegation: "Délégation",
      subDivision: "Arrondissement / Sub-Division",
      address: "Adresse",
      poBox: "BP / P.O. Box",
      logo: "Logo",
      saveHeader: "Sauvegarder l'en-tête",
    },
    en: {
      title: "Class Enrollments",
      subtitle: "Enrollment situation by class — Academic year",
      listTitle: "CLASS ENROLLMENTS",
      configTitle: "Configuration",
      section: "Section", cycle: "Cycle", level: "Level",
      allSections: "All sections", allCycles: "All cycles",
      allLevels: "All levels",
      academicYear: "Academic year",
      exportPDF: "PDF", exportExcel: "Excel", exportExcelData: "Excel Data",
      noData: "No data", loading: "Loading...",
      noYearSelected: "No academic year selected",
      yearRequired: "Please select an academic year in the header",
      classCol: "Class", total: "Total", levelTotal: "Level", grandTotal: "Secondary",
      sex: "GENDER", male: "Male", female: "Female",
      repeating: "REPEATING", repYes: "Yes", repNo: "No",
      situation: "SITUATION", newStudent: "New", oldStudent: "Former",
      selectClasses: "Select classes",
      selectAll: "Select all", deselectAll: "Deselect all",
      selectedCount: "class(es) selected",
      status: "Status", enrolled: "Enrolled", globalTotal: "Global Total",
      headerTitle: "School Header",
      headerDesc: "Customize the header that appears on each document",
      schoolName: "School Name",
      schoolNameEn: "Name (EN)",
      region: "Region",
      department: "Department",
      delegation: "Delegation",
      subDivision: "Sub Division",
      address: "Address",
      poBox: "P.O. Box",
      logo: "Logo",
      saveHeader: "Save Header",
    }
  }[lang];

  const [sections, setSections] = useState<any[]>([]);
  const [cycles, setCycles] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [years, setYears] = useState<any[]>([]);

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");

  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [diagInfo, setDiagInfo] = useState<string>("");
  const [selectedClassIds, setSelectedClassIds] = useState<Set<number>>(new Set());

  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [schoolHeader, setSchoolHeader] = useState<SchoolHeader>({
    republic_fr: "RÉPUBLIQUE DU CAMEROUN",
    republic_en: "REPUBLIC OF CAMEROON",
    motto_fr: "Paix-Travail-Patrie",
    motto_en: "Peace-Work-Fatherland",
    region: "RÉGION DE L'OUEST",
    department: "DÉPARTEMENT DE LA MIFI",
    delegation: "DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES",
    sub_division: "ARRONDISSEMENT DE BAFOUSSAM II",
    school_name: "LYCÉE BILINGUE DE BALENG",
    school_name_en: "GBHS BALENG",
    address: "BP : 1041 Baleng",
    po_box: "P.O. Box : 1041 Baleng",
    logo_url: "",
  });

  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (schoolId) params.school_id = schoolId;
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
    return params;
  }, [schoolId, selectedAcademicYearId]);

  // ==================== FILTRES PROGRESSIFS ====================
  const filteredCycles = useMemo(() => {
    if (!selectedSection) return cycles;
    return cycles.filter((c: any) => String(c.section_id) === selectedSection);
  }, [cycles, selectedSection]);

  const filteredLevels = useMemo(() => {
    if (!selectedCycle) {
      if (!selectedSection) return levels;
      const cycleIds = cycles
        .filter((c: any) => String(c.section_id) === selectedSection)
        .map((c: any) => String(c.id));
      return levels.filter((l: any) => cycleIds.includes(String(l.cycle_id)));
    }
    return levels.filter((l: any) => String(l.cycle_id) === selectedCycle);
  }, [levels, cycles, selectedCycle, selectedSection]);

  const filteredClasses = useMemo(() => {
    if (selectedLevel) {
      return classes.filter((c: any) => String(c.level_id) === selectedLevel || String(c.levelId) === selectedLevel);
    }
    if (selectedCycle) {
      const levelIds = filteredLevels.map((l: any) => String(l.id));
      return classes.filter((c: any) => levelIds.includes(String(c.level_id)) || levelIds.includes(String(c.levelId)));
    }
    if (selectedSection) {
      const cycleIds = filteredCycles.map((c: any) => String(c.id));
      const levelIds = levels
        .filter((l: any) => cycleIds.includes(String(l.cycle_id)))
        .map((l: any) => String(l.id));
      return classes.filter((c: any) => levelIds.includes(String(c.level_id)) || levelIds.includes(String(c.levelId)));
    }
    return classes;
  }, [classes, selectedLevel, selectedCycle, selectedSection, filteredLevels, levels, filteredCycles]);

  // Synchroniser selectedClassIds avec les classes visibles
  useEffect(() => {
    const visibleIds = new Set(filteredClasses.map((c: any) => Number(c.id)));
    setSelectedClassIds(prev => {
      const next = new Set<number>();
      prev.forEach(id => { if (visibleIds.has(id)) next.add(id); });
      if (next.size === 0 && filteredClasses.length > 0) {
        filteredClasses.forEach((c: any) => next.add(Number(c.id)));
      }
      return next;
    });
  }, [filteredClasses]);

  const toggleClass = (id: number) => {
    const next = new Set(selectedClassIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedClassIds(next);
  };
  const selectAllClasses = () => setSelectedClassIds(new Set(filteredClasses.map((c: any) => Number(c.id))));
  const deselectAllClasses = () => setSelectedClassIds(new Set());

  // ==================== CHARGEMENT DES DONNÉES ====================
  useEffect(() => {
    const loadAllData = async () => {
      if (!schoolId || !selectedAcademicYearId) return;
      setLoading(true);
      try {
        const params = getFilterParams();
        const [yearRes, secRes, cycRes, levRes, clsRes] = await Promise.all([
          apiService.get(`/schools/${schoolId}/academic-years`).catch(() => ({ data: [] })),
          apiService.get("/sections", { params }).catch(() => ({ data: [] })),
          apiService.get("/cycles", { params }).catch(() => ({ data: [] })),
          apiService.get("/levels", { params }).catch(() => ({ data: [] })),
          apiService.get("/classes", { params }).catch(() => ({ data: [] })),
        ]);

        setYears(extractData(yearRes));
        setSections(extractData(secRes));
        setCycles(extractData(cycRes));
        setLevels(extractData(levRes));
        const loadedClasses = extractData(clsRes);
        setClasses(loadedClasses);

        // ══════════ V7 — MÊME SOURCE QUE StudentsPage (parité garantie) ══════════
        // Form 5B2 doit afficher 60 (élèves actifs de l'année), pas 75.
        // Même endpoint /students, mêmes paramètres (active_only=true),
        // pagination complète. L'endpoint par classe est abandonné.
        const OUT_STATUSES = ['DEMISSION', 'EXCLU', 'RENVOYE', 'TRANSFERE', 'DIPLOME', 'ANCIEN', 'INACTIVE'];
        let all: Student[] = [];
        const seenIds = new Set<number>();
        let apiTotal = 0;
        let pageN = 1;
        while (pageN <= 60) { // 60 pages × 200 = 12 000 élèves max
          const res: any = await apiService.get("/students", {
            params: { ...params, active_only: true, page: pageN, per_page: 200, _ts: Date.now() },
          });
          const arr = extractData(res);
          apiTotal = (res && typeof res.total === "number") ? res.total
            : (res?.data && typeof res.data.total === "number") ? res.data.total
            : arr.length;
          for (const s of arr) {
            const stu = normalizeStudent(s);
            if (!stu.id || seenIds.has(stu.id)) continue;
            // class_id absent → résolution par nom de classe
            if ((stu.class_id === undefined || stu.class_id === null) && stu.class_name) {
              const clsObj = loadedClasses.find((c: any) => c.name === stu.class_name);
              if (clsObj) stu.class_id = Number(clsObj.id);
            }
            seenIds.add(stu.id);
            all.push(stu);
          }
          if (arr.length === 0 || all.length >= apiTotal) break;
          pageN++;
        }

        // Sécurité : exclusion des statuts sortants même si le backend
        // n'avait pas appliqué active_only.
        const beforeFilter = all.length;
        all = all.filter(s => !s.status || !OUT_STATUSES.includes(String(s.status).toUpperCase()));
        if (beforeFilter - all.length > 0) console.log(`[Effectifs] ${beforeFilter - all.length} élève(s) sortant(s) exclu(s) en sécurité`);

        // DIAGNOSTIC visible + console
        const withoutClass = all.filter(s => s.class_id === undefined).length;
        console.log(`[Effectifs] ${loadedClasses.length} classes | ${all.length} élève(s) actif(s) via /students (total API: ${apiTotal}) | ${withoutClass} sans class_id`);
        setDiagInfo(`${loadedClasses.length} classe(s) • ${all.length} élève(s) actif(s) — source identique à la page Élèves • ${withoutClass} sans classe`);
        setAllStudents(all);
      } catch (e) {
        console.error("Load error:", e);
        setAllStudents([]);
      } finally {
        setLoading(false);
      }
    };
    loadAllData();
  }, [schoolId, selectedAcademicYearId, getFilterParams]);

  // Effectif par classe (affiché à côté de chaque case à cocher)
  const classCounts = useMemo(() => {
    const m = new Map<number, number>();
    allStudents.forEach(s => {
      if (s.class_id !== undefined && s.class_id !== null) {
        const cid = Number(s.class_id);
        m.set(cid, (m.get(cid) || 0) + 1);
      }
    });
    return m;
  }, [allStudents]);

  // ==================== STATISTIQUES ====================
  const stats = useMemo(() => {
    if (selectedClassIds.size === 0) return [];
    const groups: Record<number, ClassStats> = {};
    allStudents.forEach(s => {
      const cid = s.class_id !== undefined && s.class_id !== null ? Number(s.class_id) : 0;
      if (!selectedClassIds.has(cid)) return;
      if (!groups[cid]) {
        const cls = classes.find((c: any) => c.id === cid);
        const lvl = cls ? levels.find((l: any) => l.id === cls.level_id) : null;
        groups[cid] = {
          class_id: cid,
          class_name: cls?.name || s.class_name || `Classe ${cid}`,
          level_name: lvl?.label_fr || lvl?.name || "",
          total: 0, male: 0, female: 0,
          repeating_yes: 0, repeating_no: 0,
          new_student: 0, old_student: 0,
        };
      }
      groups[cid].total++;
      const g = (s.gender || "").toUpperCase();
      if (g === "M") groups[cid].male++;
      else if (g === "F") groups[cid].female++;
      if (s.is_repeating) groups[cid].repeating_yes++;
      else groups[cid].repeating_no++;
      if (s.is_new) groups[cid].new_student++;
      else groups[cid].old_student++;
    });
    return Object.values(groups);
  }, [allStudents, selectedClassIds, classes, levels]);

  const groupedStats = useMemo(() => {
    const groups: Record<string, ClassStats[]> = {};
    stats.forEach(s => {
      if (!groups[s.level_name]) groups[s.level_name] = [];
      groups[s.level_name].push(s);
    });
    return groups;
  }, [stats]);

  const grandTotal = useMemo(() => ({
    total: stats.reduce((s, c) => s + c.total, 0),
    male: stats.reduce((s, c) => s + c.male, 0),
    female: stats.reduce((s, c) => s + c.female, 0),
    repeating_yes: stats.reduce((s, c) => s + c.repeating_yes, 0),
    repeating_no: stats.reduce((s, c) => s + c.repeating_no, 0),
    new_student: stats.reduce((s, c) => s + c.new_student, 0),
    old_student: stats.reduce((s, c) => s + c.old_student, 0),
  }), [stats]);

  const yearLabel = years.find((y: any) => y.id === selectedAcademicYearId)?.label || years.find((y: any) => y.id === selectedAcademicYearId)?.name || currentAcademicYear?.name || "2025-2026";

  // ==================== SAUVEGARDE EN-TÊTE ====================
  const handleSaveHeader = () => {
    localStorage.setItem("lybibal-school-header", JSON.stringify(schoolHeader));
    toast.success(lang === "fr" ? "En-tête sauvegardé" : "Header saved");
  };

  // ==================== EXPORT CSV ====================
  const handleExportCSV = () => {
    const headerRows = [
      [schoolHeader.school_name],
      [t.listTitle],
      [`${t.academicYear}: ${yearLabel} | ${t.status}: ${t.enrolled}`],
      [],
    ];
    const rows: string[][] = [];
    rows.push([t.classCol, t.total, t.male, t.female, t.repYes, t.repNo, t.newStudent, t.oldStudent]);
    Object.entries(groupedStats).forEach(([level, items]) => {
      items.forEach(s => rows.push([s.class_name, String(s.total), String(s.male), String(s.female), String(s.repeating_yes), String(s.repeating_no), String(s.new_student), String(s.old_student)]));
      const lt = items.reduce((a, b) => ({
        total: a.total + b.total, male: a.male + b.male, female: a.female + b.female,
        repeating_yes: a.repeating_yes + b.repeating_yes, repeating_no: a.repeating_no + b.repeating_no,
        new_student: a.new_student + b.new_student, old_student: a.old_student + b.old_student
      }), { total: 0, male: 0, female: 0, repeating_yes: 0, repeating_no: 0, new_student: 0, old_student: 0 });
      rows.push([`${t.levelTotal} ${level}`, String(lt.total), String(lt.male), String(lt.female), String(lt.repeating_yes), String(lt.repeating_no), String(lt.new_student), String(lt.old_student)]);
    });
    rows.push([t.grandTotal, String(grandTotal.total), String(grandTotal.male), String(grandTotal.female), String(grandTotal.repeating_yes), String(grandTotal.repeating_no), String(grandTotal.new_student), String(grandTotal.old_student)]);
    downloadCSV(`effectifs-classes-${yearLabel}.csv`, rows, headerRows);
    toast.success("Export CSV généré");
  };

  // ==================== EXPORT EXCEL DATA ====================
  const handleExportExcelData = () => {
    const headerData = [
      { [t.classCol]: "", [t.total]: schoolHeader.school_name, [t.male]: "", [t.female]: "", [t.repYes]: "", [t.repNo]: "", [t.newStudent]: "", [t.oldStudent]: "" },
      { [t.classCol]: "", [t.total]: t.listTitle, [t.male]: "", [t.female]: "", [t.repYes]: "", [t.repNo]: "", [t.newStudent]: "", [t.oldStudent]: "" },
      { [t.classCol]: "", [t.total]: `${t.academicYear}: ${yearLabel}`, [t.male]: "", [t.female]: "", [t.repYes]: "", [t.repNo]: "", [t.newStudent]: "", [t.oldStudent]: "" },
    ];
    const data = stats.map(s => ({
      [t.classCol]: s.class_name, [t.total]: s.total, [t.male]: s.male, [t.female]: s.female,
      [t.repYes]: s.repeating_yes, [t.repNo]: s.repeating_no, [t.newStudent]: s.new_student, [t.oldStudent]: s.old_student,
    }));
    downloadExcelData(`effectifs-data-${yearLabel}.csv`, [...headerData, ...data]);
    toast.success("Excel Data exporté");
  };

  // ==================== IMPRESSION PDF ====================
  const handlePrintPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) { toast.error("Autorisez les popups"); return; }
    const today = new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB");

    const logoHtml = schoolHeader.logo_url
      ? `<img src="${schoolHeader.logo_url}" style="width:55px;height:55px;object-fit:contain;" />`
      : `<div style="width:50px;height:50px;background:#1e3a5f;border-radius:50%;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:14px;font-weight:bold;">LB</span></div>`;

    let rowsHTML = "";
    Object.entries(groupedStats).forEach(([level, items]) => {
      items.forEach((s, i) => {
        rowsHTML += `<tr style="background:${i % 2 === 0 ? "#fff" : "#f9fafb"};">
          <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;font-weight:600;">${s.class_name}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.total}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.male}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.female}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.repeating_yes}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.repeating_no}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.new_student}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.old_student}</td>
        </tr>`;
      });
      const lt = items.reduce((a, b) => ({
        total: a.total + b.total, male: a.male + b.male, female: a.female + b.female,
        repeating_yes: a.repeating_yes + b.repeating_yes, repeating_no: a.repeating_no + b.repeating_no,
        new_student: a.new_student + b.new_student, old_student: a.old_student + b.old_student
      }), { total: 0, male: 0, female: 0, repeating_yes: 0, repeating_no: 0, new_student: 0, old_student: 0 });
      rowsHTML += `<tr style="background:#e5e7eb;font-weight:bold;">
        <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;">${t.levelTotal} ${level}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.total}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.male}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.female}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.repeating_yes}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.repeating_no}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.new_student}</td>
        <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${lt.old_student}</td>
      </tr>`;
    });
    rowsHTML += `<tr style="background:#1e3a5f;color:#fff;font-weight:bold;">
      <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;">${t.grandTotal}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.total}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.male}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.female}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.repeating_yes}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.repeating_no}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.new_student}</td>
      <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${grandTotal.old_student}</td>
    </tr>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t.title}</title>
      <style>*{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4 portrait;margin:10mm}body{font-family:Arial,sans-serif;margin:0;padding:10px;font-size:11px}table{width:100%;border-collapse:collapse;font-size:10px}th{background:#1e3a5f;color:#fff;padding:6px;border:1px solid #ddd;text-align:center}td{border:1px solid #ddd;padding:4px 6px}.footer{margin-top:15px;text-align:center;font-size:9px;color:#999;border-top:1px solid #eee;padding-top:8px}@media print{.no-print{display:none}}</style></head><body>
      <div style="text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:10px;margin-bottom:15px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;text-align:left;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_fr}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${schoolHeader.motto_fr}</p>
            <p style="margin:4px 0 1px 0;font-size:9px;color:#333;">${schoolHeader.region}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.department}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.delegation}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.sub_division}</p>
          </div>
          <div style="width:70px;text-align:center;margin:0 12px;flex-shrink:0;">${logoHtml}</div>
          <div style="flex:1;text-align:right;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_en}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${schoolHeader.motto_en}</p>
            <p style="margin:4px 0 1px 0;font-size:9px;color:#333;">${schoolHeader.region.replace("RÉGION DE L'", "WEST ").replace("RÉGION", "REGION")}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.department.replace("DÉPARTEMENT DE LA ", "").replace("DÉPARTEMENT", "DIVISION")}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${headerEnDelegation(schoolHeader.delegation)}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.sub_division.replace("ARRONDISSEMENT", "SUB DIVISION")}</p>
          </div>
        </div>
        <div style="margin-top:8px;">
          <p style="margin:0;font-size:14px;font-weight:bold;color:#1e3a5f;">${schoolHeader.school_name}</p>
          <p style="margin:2px 0;font-size:11px;font-weight:600;color:#1e3a5f;">${schoolHeader.school_name_en}</p>
          <p style="margin:2px 0;font-size:9px;color:#666;">${schoolHeader.address} | ${schoolHeader.po_box}</p>
        </div>
      </div>
      <h1 style="text-align:center;font-size:22px;font-weight:bold;color:#1e3a5f;margin:12px 0;text-transform:uppercase;letter-spacing:1px;">${t.listTitle}</h1>
      <div style="display:flex;justify-content:space-between;margin-bottom:10px;font-size:11px;">
        <div><strong>${t.academicYear}:</strong> ${yearLabel}</div>
        <div><strong>${t.status}:</strong> ${t.enrolled}</div>
        <div><strong>${t.globalTotal}:</strong> ${grandTotal.total}</div>
      </div>
      <table><thead><tr><th>${t.classCol}</th><th>${t.total}</th><th>${t.male}</th><th>${t.female}</th><th>${t.repYes}</th><th>${t.repNo}</th><th>${t.newStudent}</th><th>${t.oldStudent}</th></tr></thead><tbody>${rowsHTML}</tbody></table>
      <div class="footer">${schoolHeader.school_name} — ${t.title} — ${today} — Téléchargé sur https://myhelperschool.cm/</div>
      <div class="no-print" style="text-align:center;margin-top:15px;"><button onclick="window.print()" style="padding:8px 24px;background:#1e3a5f;color:#fff;border:none;border-radius:4px;cursor:pointer;">${lang === "fr" ? "Imprimer" : "Print"}</button></div>
    </body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // ==================== INIT ====================
  useEffect(() => {
    const yearId = getSelectedYearId();
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY);
    if (yearId) {
      setSelectedAcademicYearId(yearId);
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel });
    }
    try {
      const storedSchool = localStorage.getItem("lybibal-selected-school-id");
      if (storedSchool) setSchoolId(Number(storedSchool));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.year_id) {
        setSelectedAcademicYearId(Number(detail.year_id));
        if (detail.label) setCurrentAcademicYear({ id: Number(detail.year_id), name: detail.label });
        setAllStudents([]);
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("lybibal-school-header");
      if (saved) setSchoolHeader(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {currentAcademicYear.name}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT PANEL */}
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#1e3a8a]" />
              {t.configTitle}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.academicYear}</label>
                <select value={selectedAcademicYearId ?? ""} onChange={(e) => setSelectedAcademicYearId(e.target.value ? Number(e.target.value) : null)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{t.academicYear}</option>
                  {years.map((y: any) => <option key={y.id} value={String(y.id)}>{y.label || y.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.section}</label>
                <select value={selectedSection} onChange={(e) => { setSelectedSection(e.target.value); setSelectedCycle(""); setSelectedLevel(""); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{t.allSections}</option>
                  {sections.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name || s.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.cycle}</label>
                <select value={selectedCycle} onChange={(e) => { setSelectedCycle(e.target.value); setSelectedLevel(""); }} disabled={!selectedSection} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allCycles}</option>
                  {filteredCycles.map((c: any) => <option key={c.id} value={String(c.id)}>{c.name || c.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.level}</label>
                <select value={selectedLevel} onChange={(e) => { setSelectedLevel(e.target.value); }} disabled={!selectedCycle} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allLevels}</option>
                  {filteredLevels.map((l: any) => <option key={l.id} value={String(l.id)}>{l.name || l.label_fr}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* School Header Config */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <button onClick={() => setShowHeaderConfig(!showHeaderConfig)} className="flex items-center justify-between w-full text-sm font-semibold text-gray-900">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1e3a8a]" />{t.headerTitle}</span>
              {showHeaderConfig ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHeaderConfig && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-gray-500">{t.headerDesc}</p>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolName}</label><input value={schoolHeader.school_name} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolNameEn}</label><input value={schoolHeader.school_name_en} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name_en: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.region}</label><input value={schoolHeader.region} onChange={(e) => setSchoolHeader(p => ({ ...p, region: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.department}</label><input value={schoolHeader.department} onChange={(e) => setSchoolHeader(p => ({ ...p, department: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.delegation}</label><input value={schoolHeader.delegation} onChange={(e) => setSchoolHeader(p => ({ ...p, delegation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.subDivision}</label><input value={schoolHeader.sub_division} onChange={(e) => setSchoolHeader(p => ({ ...p, sub_division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.address}</label><input value={schoolHeader.address} onChange={(e) => setSchoolHeader(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.poBox}</label><input value={schoolHeader.po_box} onChange={(e) => setSchoolHeader(p => ({ ...p, po_box: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.logo}</label>
                  <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => setSchoolHeader(p => ({ ...p, logo_url: reader.result as string })); reader.readAsDataURL(file); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  {schoolHeader.logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={schoolHeader.logo_url} alt="Logo" className="w-12 h-12 object-contain border rounded" />
                      <button onClick={() => setSchoolHeader(p => ({ ...p, logo_url: "" }))} className="text-xs text-red-600 hover:text-red-800 underline">{lang === "fr" ? "Supprimer" : "Remove"}</button>
                    </div>
                  )}
                </div>
                <button onClick={handleSaveHeader} className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2"><Save className="w-4 h-4" />{t.saveHeader}</button>
              </div>
            )}
          </div>

          {/* Multi-class selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-[#1e3a8a]" />
                {t.selectClasses}
              </h3>
              <span className="text-xs text-gray-500">{selectedClassIds.size} {t.selectedCount}</span>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <button onClick={selectAllClasses} className="text-xs text-blue-600 hover:text-blue-700 font-medium">{t.selectAll}</button>
              <span className="text-gray-300">|</span>
              <button onClick={deselectAllClasses} className="text-xs text-gray-500 hover:text-gray-700 font-medium">{t.deselectAll}</button>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {filteredClasses.map((c: any) => (
                <label key={c.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs ${selectedClassIds.has(c.id) ? "bg-blue-50 border-blue-200" : "bg-white border-gray-200 hover:bg-gray-50"}`}>
                  {selectedClassIds.has(c.id) ? <CheckSquare className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" /> : <Square className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                  <input type="checkbox" checked={selectedClassIds.has(c.id)} onChange={() => toggleClass(c.id)} className="sr-only" />
                  <span className="font-medium text-gray-700 truncate">{c.name}</span>
                  <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${(classCounts.get(Number(c.id)) || 0) > 0 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>{classCounts.get(Number(c.id)) || 0}</span>
                </label>
              ))}
            </div>
            {filteredClasses.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{t.noData}</p>}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1" />
            <button onClick={handlePrintPDF} className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2 transition-colors"><Printer className="w-4 h-4" />{t.exportPDF}</button>
            <button onClick={handleExportCSV} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-2 transition-colors"><FileSpreadsheet className="w-4 h-4" />{t.exportExcel}</button>
            <button onClick={handleExportExcelData} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center gap-2 transition-colors"><Download className="w-4 h-4" />{t.exportExcelData}</button>
          </div>

          {diagInfo && (
            <div className="text-xs font-mono text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              🩺 Diagnostic : {diagInfo} — classes cochées : {selectedClassIds.size}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1e3a5f] text-white">
                    <th className="px-3 py-2 text-left text-xs font-semibold" rowSpan={2}>{t.classCol}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold" colSpan={3}>{t.sex}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold" colSpan={2}>{t.repeating}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold" colSpan={2}>{t.situation}</th>
                  </tr>
                  <tr className="bg-[#1e3a5f] text-white">
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.total}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.male}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.female}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.repYes}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.repNo}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.newStudent}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.oldStudent}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /><p className="text-sm text-gray-500 mt-2">{t.loading}</p></td></tr>
                  ) : stats.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500"><Users className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.noData}</p></td></tr>
                  ) : (
                    <>
                      {Object.entries(groupedStats).map(([level, items]) => (
                        <React.Fragment key={level}>
                          {items.map(s => (
                            <tr key={s.class_id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-3 py-2 font-medium text-gray-900 text-xs">{s.class_name}</td>
                              <td className="px-3 py-2 text-center text-gray-700 font-semibold text-xs">{s.total}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{s.male}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{s.female}</td>
                              <td className="px-3 py-2 text-center text-amber-600 text-xs">{s.repeating_yes}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{s.repeating_no}</td>
                              <td className="px-3 py-2 text-center text-emerald-600 text-xs">{s.new_student}</td>
                              <td className="px-3 py-2 text-center text-gray-500 text-xs">{s.old_student}</td>
                            </tr>
                          ))}
                          {(() => {
                            const lt = items.reduce((a, b) => ({ total: a.total + b.total, male: a.male + b.male, female: a.female + b.female, repeating_yes: a.repeating_yes + b.repeating_yes, repeating_no: a.repeating_no + b.repeating_no, new_student: a.new_student + b.new_student, old_student: a.old_student + b.old_student }), { total: 0, male: 0, female: 0, repeating_yes: 0, repeating_no: 0, new_student: 0, old_student: 0 });
                            return (
                              <tr className="bg-gray-100 font-semibold">
                                <td className="px-3 py-2 text-gray-700 text-xs">{t.levelTotal} {level}</td>
                                <td className="px-3 py-2 text-center text-gray-900 text-xs">{lt.total}</td>
                                <td className="px-3 py-2 text-center text-gray-700 text-xs">{lt.male}</td>
                                <td className="px-3 py-2 text-center text-gray-700 text-xs">{lt.female}</td>
                                <td className="px-3 py-2 text-center text-amber-700 text-xs">{lt.repeating_yes}</td>
                                <td className="px-3 py-2 text-center text-gray-700 text-xs">{lt.repeating_no}</td>
                                <td className="px-3 py-2 text-center text-emerald-700 text-xs">{lt.new_student}</td>
                                <td className="px-3 py-2 text-center text-gray-700 text-xs">{lt.old_student}</td>
                              </tr>
                            );
                          })()}
                        </React.Fragment>
                      ))}
                      <tr className="bg-[#1e3a5f] text-white font-bold">
                        <td className="px-3 py-2 text-xs">{t.grandTotal}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.total}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.male}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.female}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.repeating_yes}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.repeating_no}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.new_student}</td>
                        <td className="px-3 py-2 text-center text-xs">{grandTotal.old_student}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}